import {
  Accordion,
  AccordionTypes,
  Button,
  Icon,
  IconButton,
  SearchInput,
  Text,
  useConfirm,
  HStack,
  VStack,
} from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation, RouteProp, useRoute } from '@react-navigation/native';
import { Box, Menu } from 'native-base';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { LayoutChangeEvent, View } from 'react-native';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../routes/types';
import {
  fundComparatorProductsSearchSelector,
  fundComparatorProductsSelector,
} from '../../../../store/product/selectors';
import { FundComparatorProductSearchResultNormalized } from '../../../../store/product/types';

import { FundComparatorChart } from './components/FundComparatorChart';
import { FundComparatorHeader } from './components/FundComparatorHeader';
import { FundComparatorProductCard } from './components/FundComparatorProductCard';
import { fundsComparatorMaxVisibleProducts } from './constants';

const MenuTriggerPlaceholder = (triggerProps) => <View {...triggerProps} />;

export const FundsComparator = () => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const route = useRoute<RouteProp<SignedInRootStackParamList, 'FundsComparator'>>();
  const { confirm } = useConfirm();
  const [search, setSearch] = useState('');
  const [productAccordionStatus, setProductAccordionStatus] =
    useState<AccordionTypes.Status>('closed');
  const [period, setPeriod] = useState(12);
  const [products, setProducts] = useState<FundComparatorProductSearchResultNormalized[]>([]);
  const [indexes, setIndexes] = useState<string[]>([]);
  const [shouldShowAutoComplete, setShouldShowAutoComplete] = useState(false);
  const [maxProductCardWidth, setMaxProductCardWidth] = useState(0);
  const [searchContainerWidth, setSearchContainerWidth] = useState(0);
  const { data: productsSearchData, loading: productsSearchLoading } = useRecoilQuery(
    fundComparatorProductsSearchSelector({ keyword: search })
  );
  const {
    data: productsData,
    loading: productsDataLoading,
    error: productsDataError,
    refresher: productsDataRefresher,
  } = useRecoilQuery(
    fundComparatorProductsSelector({ productIds: products.map((p) => p.id), period })
  );

  useEffect(() => {
    if (route.params?.selectedOffers && route.params.selectedOffers.length > 0) {
      const initialProducts: FundComparatorProductSearchResultNormalized[] =
        route.params.selectedOffers.map((offer: any) => ({
          id: offer.productId?.toString() || offer.id?.toString() || '',
          name: offer.productNameNormalized || offer.name || '',
        }));
      setProducts(initialProducts);
    }
  }, [route.params?.selectedOffers]);

  const canExpandProductsAccordion = useMemo(() => {
    return products.length > fundsComparatorMaxVisibleProducts;
  }, [products]);

  const handleGoBack = () => {
    navigation.navigate('AppDrawer', {
      screen: 'AvailableProducts',
    });
  };

  const handleProductAccordionOpen = useCallback(() => {
    if (!canExpandProductsAccordion) {
      return;
    }

    setProductAccordionStatus('opened');
  }, [canExpandProductsAccordion]);

  const handleProductAccordionClose = useCallback(() => {
    setProductAccordionStatus('closed');
  }, []);

  const handleSearch = useCallback((term: string) => {
    setSearch(term);
    setShouldShowAutoComplete(true);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearch('');
    setShouldShowAutoComplete(false);
  }, []);

  const handleCloseAutoComplete = useCallback(() => {
    setShouldShowAutoComplete(false);
  }, []);

  const handleAddProduct = useCallback(
    (productId: string) => {
      const productToAdd = productsSearchData?.find((product) => product.id === productId);
      if (!productToAdd) {
        return;
      }
      setProducts((prevProducts) => [...prevProducts, productToAdd]);
      setShouldShowAutoComplete(false);
    },
    [productsSearchData]
  );

  const handleClearSelectedProducts = useCallback(() => {
    confirm(
      {
        title: 'Limpar ativos',
        description: 'Tem certeza que deseja limpar os ativos em comparação?',
      },
      () => {
        setProducts([]);
        setProductAccordionStatus('closed');
      }
    );
  }, [confirm]);

  const handleRemoveProduct = useCallback((productId: string) => {
    setProducts((prevProducts) => prevProducts.filter((product) => product.id !== productId));
  }, []);

  const handleProductsCardContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setMaxProductCardWidth(Math.floor((width - 24) / 4));
  }, []);

  const handleSearchContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setSearchContainerWidth(width);
  }, []);

  const visibleProductsCards = useMemo(
    () =>
      products
        .slice(0, fundsComparatorMaxVisibleProducts)
        .map((product, index) => (
          <FundComparatorProductCard
            key={product.id}
            product={product}
            productIndex={index}
            maxCardWidth={maxProductCardWidth}
            onRemove={handleRemoveProduct}
          />
        )),
    [products, maxProductCardWidth, handleRemoveProduct]
  );

  const expandedProductsCards = useMemo(
    () =>
      products
        // .slice(fundsComparatorMaxVisibleProducts)
        .map((product, index) => (
          <FundComparatorProductCard
            key={product.id}
            product={product}
            productIndex={index}
            maxCardWidth={maxProductCardWidth}
            onRemove={handleRemoveProduct}
            isMobile={isMobile}
          />
        )),
    [products, maxProductCardWidth, handleRemoveProduct, isMobile]
  );

  const canShowVisibleProducts = useMemo(() => {
    return products.length > 0 && productAccordionStatus === 'closed' && !isMobile;
  }, [products, productAccordionStatus, isMobile]);

  return (
    <VStack
      p="inset.xs"
      flex={1}
      width="full"
      maxWidth={{ base: '100%', sm: '95%', lg: '1280px' }}
      minWidth={0}
      alignSelf="center"
      space="stack.xxxs">
      <FundComparatorHeader onGoBack={handleGoBack} />
      <Box width="full" onLayout={handleSearchContainerLayout}>
        <SearchInput
          placeholder="Pesquise por ativo ou gestor"
          onSearch={handleSearch}
          onClear={handleClearSearch}
          hideSearchButtonOnDesktop
          fullWidthAlways
        />
        <Menu
          trigger={MenuTriggerPlaceholder}
          isOpen={shouldShowAutoComplete}
          onClose={handleCloseAutoComplete}
          style={{ width: searchContainerWidth }}>
          {productsSearchLoading && <Menu.Item>Buscando ativos</Menu.Item>}
          {productsSearchData?.map((product) => (
            <Menu.Item
              style={{ width: '100%' }}
              key={product.id}
              onPress={() => handleAddProduct(product.id)}>
              {product.name}
            </Menu.Item>
          ))}
        </Menu>
      </Box>
      <Accordion
        status={productAccordionStatus}
        onPress={handleProductAccordionOpen}
        onClosed={handleProductAccordionClose}>
        <Accordion.Visible
          hideDefaultIcons
          containerProps={{
            paddingX: 'inset.xs',
            paddingTop: 'inset.xs',
            paddingBottom: productAccordionStatus === 'opened' ? 'inset.xxs' : 'inset.xs',
          }}>
          {products.length > 0 ? (
            <HStack alignItems="center" space="stack.nano" justifyContent="space-between">
              <Text flex={1} variant="semibold" fontSize="lg">
                {products.length} ativo(s) em comparação
              </Text>
              {isMobile ? (
                <IconButton
                  padding={0}
                  testId="clear-selected-products-button"
                  icon="Trash"
                  iconColor="feedback.error.default"
                  iconSize="xs"
                  onPress={handleClearSelectedProducts}
                />
              ) : (
                <Button
                  rightIcon={<Icon name="Trash" size="xs" color="feedback.error.default" />}
                  onPress={handleClearSelectedProducts}
                  variant="textLink"
                  _text={{ color: 'feedback.error.default' }}>
                  LIMPAR SELEÇÃO
                </Button>
              )}
              <IconButton
                padding={0}
                testId="toggle-products-accordion-button"
                icon={productAccordionStatus === 'opened' ? 'ChevronUp' : 'ChevronDown'}
                iconColor="neutral.medium"
                onPress={
                  productAccordionStatus === 'opened'
                    ? handleProductAccordionClose
                    : handleProductAccordionOpen
                }
              />
            </HStack>
          ) : (
            <HStack alignItems="center" space="stack.nano">
              <Icon name="Info" size="xl" color="neutral.medium" />
              <VStack space="stack.nano">
                <Text variant="semibold" fontSize="lg">
                  Selecione um ativo
                </Text>
                <Text>Encontre um ativo usando o campo de busca ou volte a lista de fundos.</Text>
              </VStack>
            </HStack>
          )}
          {canShowVisibleProducts && (
            <Box
              onLayout={handleProductsCardContainerLayout}
              flexWrap="wrap"
              flex={1}
              flexDirection="row"
              style={{ gap: 8 }}>
              {visibleProductsCards}
            </Box>
          )}
        </Accordion.Visible>
        <Accordion.Expanded
          containerProps={{
            padding: 0,
            paddingTop: { base: undefined, sm: 0 },
            paddingBottom: { base: undefined, sm: 'inset.xs' },
            paddingLeft: { base: undefined, sm: 'inset.xs' },
            paddingRight: { base: undefined, sm: 'inset.xs' },
          }}
          maxContentHeight="80vh">
          <Box
            flexWrap={{ base: 'nowrap', sm: 'wrap' }}
            flexDirection={{ base: 'column', sm: 'row' }}
            flex={1}
            style={{ gap: 8 }}
            width="100%">
            {expandedProductsCards}
          </Box>
        </Accordion.Expanded>
      </Accordion>
      <FundComparatorChart
        data={productsData}
        indexes={indexes}
        period={period}
        onSelectIndexes={setIndexes}
        onSelectPeriod={setPeriod}
        isMobile={isMobile}
        isLoading={productsDataLoading}
        error={productsDataError}
        refresher={productsDataRefresher}
      />
    </VStack>
  );
};
